<template>
  <n-theme type="driver">
    <activity-wrapper
      ref="wrapper"
      icon="location-alt"
      :status-color="statusColor"
      :format-clock="false"
      :clock="headerText"
      :expanded="hasAwaitingApproval"
      @expandChanged="isExpanded = $event"
    >
      <template #status>
        {{ `${$tc('overview.single.driver.bookedSeats', passengersAcceptedSeats)}` }}
      </template>
      <template #steps>
        <step
          v-for="(step, index) in steps"
          :key="index"
          :time="step.time"
          :address="step.address"
        />
      </template>
      <template #action>
        <!-- Show trash icon directly if only delete action is available -->
        <n-icon
          v-if="shouldShowDirectTrashIcon"
          name="trash"
          :outline="true"
          class="action-icon"
          @click="cancelTrip"
        />
        <!-- Show popover if multiple actions are available -->
        <n-popover
          v-else
          v-slot="{ closePopover }"
        >
          <!-- Only show view trip in popover if it's not already shown as a main button -->
          <div
            v-if="shouldShowViewTripInPopover"
            class="popover-menu-item"
            @click="closePopover(openTripDetails)"
          >
            <n-icon
              name="route"
              :outline="true"
            />
            {{ $t('overview.popover.showDetails') }} 
          </div>
          <div
            v-if="shouldShowEditInPopover"
            class="popover-menu-item"
            @click="closePopover(editTrip)"
          >
            <n-icon
              name="pencil_new"
              :outline="true"
            />
            <div>{{ $t('overview.popover.edit') }}</div>
          </div>
          <div
            class="popover-menu-item"
            @click="closePopover(cancelTrip)"
          >
            <n-icon
              name="trash"
              :outline="true"
            />
            <div>{{ $t('overview.popover.deleteTrip') }}</div>
          </div>
        </n-popover>
      </template>
      <template #extra>
        <n-column
          :span="6"
          class="flex-v-center"
        >
          <seats
            :accepted="passengersAcceptedSeats"
            :waiting="passengersAwaitingApprovalSeats"
            :total="driverTrip.seats"
          />
          <n-text
            :color="hasAwaitingApproval ? 'warning' : 'accent-faded'"
            preset="title"
          >
            {{ `${$tc('overview.single.driver.bookedSeats', passengersAcceptedSeats)}` }}
          </n-text>
        </n-column>
      </template>

      <template #buttons>
        <div
          class="justify-between span-6"
          style="column-gap: 10px;"
        >
          <n-button
            v-if="canShareRide && !isFullyBooked"
            class="flex-1"
            size="lg"
            icon="share"
            color="secondary"
            icon-custom-size="14"
            @click="shareTrip(driverTrip.id)"
          >
            {{ $t('overview.buttons.share') }}
          </n-button>
          <n-button
            v-if="hasAwaitingApproval"
            class="flex-1"
            size="lg"
            color="warning"
            type="filled"
            @click="openTripDetails"
          >
            {{ $t('overview.buttons.showDetails') }}
          </n-button>
          <n-button
            v-if="isQueued && !hasAwaitingApproval && hasBookedSeats"
            class="flex-1"
            size="lg"
            type="outlined"
            inverted
            @click="openTripDetails"
          >
            {{ $t('overview.buttons.showDetails') }}
          </n-button>
          <n-button
            v-if="isQueued && !hasAwaitingApproval && !hasBookedSeats"
            class="flex-1"
            size="lg"
            type="outlined"
            inverted
            color="default"
            icon="pencil_new"
            icon-custom-size="14"
            @click="editTrip"
          >
            {{ $t('overview.buttons.edit') }}
          </n-button>
          <n-button
            v-if="!isQueued && !hasAwaitingApproval"
            class="flex-1"
            size="lg"
            :inverted="isQueued"
            :type="isQueued ? 'outlined' : 'filled'"
            :loading="isActivating"
            @click="activateTrip"
          >
            {{ $t('overview.buttons.start') }}
          </n-button>
        </div>
      </template>
    </activity-wrapper>
  </n-theme>
</template>

<script>

import store from "@/store";
import ActivityWrapper from '@/components/shared/overview/activityWrapper';
import Step from '@/components/shared/overview/parts/step';
import Seats from '@/components/shared/overview/parts/seats';
import { formatRelativeWithoutTime } from '@/vendor/date-fns';
import nativeBridge from '@/native-bridge';

export default {
    name: '',
    components: {
        ActivityWrapper, Step, Seats
    },
    props: {
        driverTrip: {
            type: Object,
            required: true,
        },
        statusColor: {
          type: String,
          default: 'accent',
        },
        passengersAwaitingApproval: {
          type: Array,
          default: null
        },
        passengersAwaitingApprovalSeats: {
          type: Number,
          default: 0
        },
        passengersAcceptedSeats: {
          type: Number,
          default: 0
        },
        steps: {
          type: Array,
          default: null
        },
        openTrip: {
          type: Function,
          default: null,
        },
        isQueued: {
          type: Boolean,
          default: false,
        },
        editTrip: {
          type: Function,
          default: null,
        },
        canRateTrip: {
          type: Boolean,
          default: null,
        },
        isActivating: {
          type: Boolean,
          default: false,
        },
        activateTrip: {
          type: Function,
          default: null,
        },
        openTripDetails: {
          type: Function,
          default: null,
        },
        cancelTrip: {
          type: Function,
          default: null,
        }
    },
    computed: {
      headerText() {
          return formatRelativeWithoutTime(this.driverTrip.planned_departure);
      },
      canShareRide() {
        return (store.getters['app/HAS_EXPO_CAPABILITY']('ShareSheet') && store.getters['user/HAS_FEATURE_FLAG']('ShareRides'));
      },
      hasAwaitingApproval() {
        return this.passengersAwaitingApprovalSeats > 0
      },
      isFullyBooked() {
        return this.passengersAcceptedSeats >= this.driverTrip.seats;
      },
      hasBookedSeats() {
        return this.passengersAcceptedSeats > 0;
      },
      shouldShowDirectTrashIcon() {
        // Show direct trash icon when only delete would be available in popover
        // This happens when neither view trip nor edit would be shown in popover
        return !this.shouldShowViewTripInPopover && !this.shouldShowEditInPopover;
      },
      shouldShowViewTripInPopover() {
        // Only show view trip in popover if it's NOT already shown as a main button
        const viewTripShownAsMainButton = this.hasAwaitingApproval || (this.isQueued && !this.hasAwaitingApproval && this.hasBookedSeats);
        return !viewTripShownAsMainButton;
      },
      shouldShowEditInPopover() {
        // Show edit in popover only when edit is available but NOT already shown as main button
        const editAvailable = !this.hasAwaitingApproval && !this.hasBookedSeats;
        const editShownAsMainButton = this.isQueued && !this.hasAwaitingApproval && !this.hasBookedSeats;
        return editAvailable && !editShownAsMainButton;
      }
    },
    methods: {
      shareTrip(tripId) {
        nativeBridge.send.openShareSheet(tripId);
      }
    }
};
</script>

<style lang="scss" scoped>
.footer-disclaimer {
    color: var(--color-orange);
    font-size: 10px;
    text-align: center;
}

.action-icon {
    color: var(--color-grey);
}
</style>
